import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { announcementService } from "@/features/announcements/services/feedService";
import { sendingOrgService } from "@/features/return-home/services/sendingOrgService";
import {
  getPresignedUrl,
  uploadToS3Direct,
  confirmAttachmentInDB,
  copySharepointFileToRelation,
} from "@/shared/services/uploadService";
import { getMimeType } from "@/shared/utils/fileHelpers";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Input } from "@/shared/components/ui/Input";
import { Button } from "@/shared/components/ui/Button";
import { AudiencePreview } from "@/features/announcements/components/AudiencePreview";
import { AttachmentDropZone } from "@/features/announcements/components/AttachmentDropZone";
import { AttachmentList } from "@/features/announcements/components/AttachmentList";
import { FilesPickerModal } from "@/features/announcements/components/FilesPickerModal";
import type { SharepointFile } from "@/features/files/types/sharepoint";
import type {
  Announcement,
  AnnouncementFormData,
  AnnouncementAttachment,
  AttachmentUploadItem,
} from "@/features/announcements/types/feed";

interface CompanyOption {
  value: string;
  label: string;
}

const COUNTRY_OPTIONS = [
  { code: "JP", key: "country_jp" },
  { code: "PH", key: "country_ph" },
  { code: "VN", key: "country_vn" },
  { code: "ID", key: "country_id" },
  { code: "MM", key: "country_mm" },
  { code: "KH", key: "country_kh" },
  { code: "TH", key: "country_th" },
  { code: "CN", key: "country_cn" },
  { code: "NP", key: "country_np" },
  { code: "IN", key: "country_in" },
  { code: "BD", key: "country_bd" },
  { code: "LK", key: "country_lk" },
];

/** Returns date in YYYY-MM-DD format for today + X days */
const getFutureDateString = (daysAhead: number) => {
  const date = new Date();
  date.setDate(date.getDate() + daysAhead);
  return date.toISOString().split("T")[0];
};

const toDateTimeLocal = (value: string | null | undefined) => {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const getEmptyForm = (userData: any): AnnouncementFormData => {
  const isSouser = userData?.userType?.toLowerCase() === "souser";
  return {
    title: "",
    content: "",
    company: [],
    batch_no: "",
    country: isSouser && userData?.souser_country ? [userData.souser_country] : [],
    sending_org: isSouser && userData?.souser_sending_org ? userData.souser_sending_org : "",
    date_from: new Date().toISOString().split("T")[0],
    date_to: getFutureDateString(7),
    active: true,
    comments_on: true,
  };
};

const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
  "video/quicktime",
];
const ACCEPTED_ATTACHMENT_EXTENSIONS = ".pdf,.jpg,.jpeg,.png,.gif,.mp4,.mov";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_VIDEO_FILE_SIZE = 500 * 1024 * 1024; // 500 MB

export const AnnouncementEditPage = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  // Page-level loading for fetching an existing announcement
  const [pageLoading, setPageLoading] = useState(isEditing);
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);

  const emptyForm = getEmptyForm(userData);
  const [form, setForm] = useState<AnnouncementFormData>(emptyForm);
  const isSouser = userData?.userType?.toLowerCase() === "souser";
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [sendingOrgs, setSendingOrgs] = useState<{ value: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pollErrors, setPollErrors] = useState<string[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [audienceOfficersOnly, setAudienceOfficersOnly] = useState(false);
  const [audienceLoading, setAudienceLoading] = useState(false);

  const [deleting, setDeleting] = useState(false);

  // Attachment-related state
  const [savedAnnouncementId, setSavedAnnouncementId] = useState<number | null>(null);
  const [showSavedBanner, setShowSavedBanner] = useState(false);
  const [attachments, setAttachments] = useState<AnnouncementAttachment[]>([]);
  const [uploads, setUploads] = useState<AttachmentUploadItem[]>([]);
  const [filesPickerOpen, setFilesPickerOpen] = useState(false);

  // Load companies on mount
  useEffect(() => {
    if (isSouser) return;
    announcementService
      .getCompaniesWithUsers()
      .then(setCompanies)
      .catch(console.error);
  }, [isSouser]);

  // Load announcement when editing
  useEffect(() => {
    if (!id) {
      setPageLoading(false);
      return;
    }

    (async () => {
      try {
        const data = await announcementService.getAnnouncements(undefined, true);
        const found = Array.isArray(data)
          ? data.find((a: Announcement) => String(a.row_id) === id)
          : null;
        if (found) {
          setAnnouncement(found);
          setSavedAnnouncementId(found.row_id);
          // Load attachments
          try {
            const atts = await announcementService.getAnnouncementAttachments(found.row_id);
            setAttachments(atts);
          } catch {
            // OK if no attachments endpoint
          }
        }
      } catch (err) {
        console.error("Failed to load announcement:", err);
      } finally {
        setPageLoading(false);
      }
    })();
  }, [id]);

  // Resolve company values — the announcement stores labels (names)
  // but the form and API need values (UUIDs).
  const resolveCompanyValues = (raw: unknown[]): string[] => {
    const cleaned = raw
      .map((v: unknown) => {
        if (typeof v === "string") return v;
        if (v && typeof v === "object") {
          const obj = v as Record<string, unknown>;
          return String(
            obj.value ?? obj.company_id ?? obj.label ?? obj.company_name ?? "",
          );
        }
        return "";
      })
      .filter(Boolean);
    if (!cleaned.length || !companies.length) return cleaned;

    const valueSet = new Set(companies.map((c) => c.value));
    if (cleaned.every((v) => valueSet.has(v))) return cleaned;

    const lookupMap = new Map<string, string>();
    for (const c of companies) {
      if (c.value) lookupMap.set(c.value, c.value);
      if (c.label) {
        lookupMap.set(c.label, c.value);
        lookupMap.set(c.label.toLowerCase(), c.value);
        lookupMap.set(c.label.trim().toLowerCase(), c.value);
      }
    }

    return cleaned
      .map(
        (v) =>
          lookupMap.get(v) ??
          lookupMap.get(v.toLowerCase()) ??
          lookupMap.get(v.trim().toLowerCase()) ??
          v,
      )
      .filter(Boolean);
  };

  // Populate form when announcement & companies are loaded
  useEffect(() => {
    if (announcement && (isSouser || companies.length > 0)) {
      const rawCompanies =
        announcement.target_companies || announcement.company || [];
      const resolvedIds = resolveCompanyValues(rawCompanies);
      setForm({
        title: announcement.title,
        content: announcement.content_text || announcement.content,
        company: resolvedIds,
        batch_no: announcement.batch_no || "",
        country: announcement.country || [],
        sending_org: announcement.sending_org || "",
        date_from: announcement.date_from?.slice(0, 10) || "",
        date_to: announcement.date_to?.slice(0, 10) || "",
        active: announcement.active,
        comments_on: announcement.comments_on,
        ...(announcement.poll ? { poll: {
          question: announcement.poll.question,
          options: [...announcement.poll.options].sort((a, b) => a.sort_order - b.sort_order).map((option) => ({ label: option.label, requires_note: option.requires_note })),
          allow_multiple: announcement.poll.allow_multiple,
          closes_at: toDateTimeLocal(announcement.poll.closes_at),
        }} : {}),
      });
    } else if (!announcement) {
      setForm(emptyForm);
      setSavedAnnouncementId(null);
      setAttachments([]);
      setUploads([]);
    }
    setError("");
    setShowSavedBanner(false);
  }, [announcement, companies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close company dropdown on outside click
  useEffect(() => {
    if (!companyDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(e.target as Node)
      ) {
        setCompanyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [companyDropdownOpen]);

  // Close country dropdown on outside click
  useEffect(() => {
    if (!countryDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target as Node)
      ) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [countryDropdownOpen]);

  // Fetch sending orgs when exactly 1 country is selected; clear otherwise
  useEffect(() => {
    if (isSouser) return;
    if (form.country.length === 1) {
      sendingOrgService
        .getDropdown(form.country[0])
        .then((data: any[]) =>
          setSendingOrgs(
            data.map((o: any) => ({ value: o.value ?? o.id, label: o.label ?? o.name })),
          ),
        )
        .catch(() => setSendingOrgs([]));
    } else {
      setSendingOrgs([]);
      setForm((prev) => ({ ...prev, sending_org: "" }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.country)]);

  // Audience preview — re-fetch whenever any audience filter changes
  useEffect(() => {
    setAudienceLoading(true);
    announcementService
      .previewAudience(
        form.company,
        form.batch_no || undefined,
        form.country.length ? form.country : undefined,
        form.sending_org || undefined,
      )
      .then((data) => {
        const raw =
          typeof data === "number" ? data : (data?.count ?? data?.length ?? 0);
        setAudienceCount(Number(raw));
        setAudienceOfficersOnly(!!data?.officers_only);
      })
      .catch(() => {
        setAudienceCount(null);
        setAudienceOfficersOnly(false);
      })
      .finally(() => setAudienceLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.company.join(","), form.batch_no, form.country.join(","), form.sending_org]);

  // Auto-dismiss saved banner
  useEffect(() => {
    if (!showSavedBanner) return;
    const timer = setTimeout(() => setShowSavedBanner(false), 3000);
    return () => clearTimeout(timer);
  }, [showSavedBanner]);


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCompanyToggle = (companyId: string) => {
    setForm((prev) => {
      const selected = prev.company.includes(companyId)
        ? prev.company.filter((c) => c !== companyId)
        : [...prev.company, companyId];
      return { ...prev, company: selected };
    });
  };

  const handleCountryToggle = (code: string) => {
    setForm((prev) => {
      const selected = prev.country.includes(code)
        ? prev.country.filter((c) => c !== code)
        : [...prev.country, code];
      return { ...prev, country: selected };
    });
  };

  const handleDelete = async () => {
    if (!savedAnnouncementId) return;
    const confirmed = window.confirm(
      t(
        "feed.management.delete_confirm",
        "Are you sure you want to delete this announcement? This action cannot be undone.",
      ),
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      await announcementService.deleteAnnouncement(savedAnnouncementId);
      navigate(-1);
    } catch (err) {
      console.error("Failed to delete announcement:", err);
      setError(t("feed.management.delete_error", "Failed to delete announcement"));
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSouser && (!userData?.announcements_write ||
      (announcement && String(announcement.created_by) !== String(userData.id)))) {
      setError(t("souser.bulletin_write_denied"));
      return;
    }
    if (!form.title.trim() || !form.content.trim()) {
      setError(t("feed.form.required_fields"));
      return;
    }

    if (form.poll && typeof form.poll === "object") {
      const errors: string[] = [];
      const options = form.poll.options.map((option) => option.label.trim());
      if (!form.poll.question.trim()) errors.push(t("feed.poll.validation_question_required"));
      if (options.length < 2 || options.length > 10 || options.some((option) => !option)) errors.push(t("feed.poll.validation_min_options"));
      if (new Set(options.map((option) => option.toLowerCase())).size !== options.length) errors.push(t("feed.poll.validation_unique_options"));
      if (errors.length) {
        setPollErrors(errors);
        return;
      }
    }

    setSaving(true);
    setError("");
    setPollErrors([]);
    try {
      const submitForm: AnnouncementFormData = form.poll && typeof form.poll === "object" ? {
        ...form,
        poll: {
          ...form.poll,
          question: form.poll.question.trim(),
          options: form.poll.options.map((option) => ({ label: option.label.trim(), requires_note: option.requires_note })),
          closes_at: form.poll.closes_at ? new Date(form.poll.closes_at).toISOString() : null,
        },
      } : form;
      let resultId: number;
      if (isEditing && announcement) {
        await announcementService.updateAnnouncement(announcement.row_id, submitForm);
        resultId = announcement.row_id;
      } else {
        const result = await announcementService.createAnnouncement(submitForm);
        resultId = result.row_id ?? result.id ?? result;
        // After creating, redirect to the edit page for the new record
        navigate(`/announcements/edit/${resultId}`, { replace: true });
      }
      setSavedAnnouncementId(resultId);
      setShowSavedBanner(true);

      // Load existing attachments for this announcement
      try {
        const existing = await announcementService.getAnnouncementAttachments(resultId);
        setAttachments(existing);
      } catch {
        // OK if no endpoint
      }
    } catch (err) {
      console.error("Failed to save announcement:", err);
      const body = axios.isAxiosError(err) ? err.response?.data as { error?: string; error_code?: string } : undefined;
      setError(body?.error_code === "api_errors.poll.has_responses" ? body.error || t(body.error_code) : t("feed.form.save_error"));
    } finally {
      setSaving(false);
    }
  };

  // ── Attachment handlers ──

  const handleFilesSelected = (files: File[]) => {
    if (!savedAnnouncementId) return;

    for (const file of files) {
      const mimeType = getMimeType(file.name);
      if (!ACCEPTED_MIME_TYPES.includes(mimeType)) {
        setError(t("feed.form.invalid_file_type"));
        continue;
      }
      const maxFileSize = mimeType.startsWith("video/")
        ? MAX_VIDEO_FILE_SIZE
        : MAX_FILE_SIZE;
      if (file.size > maxFileSize) {
        setError(t("feed.form.file_too_large"));
        continue;
      }

      const localId =
        typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : Date.now().toString() + Math.random().toString(36);

      const uploadItem: AttachmentUploadItem = {
        localId,
        file,
        status: "uploading",
        progress: 0,
      };

      setUploads((prev) => [...prev, uploadItem]);

      (async () => {
        try {
          if (!userData?.id) {
            throw new Error("User not authenticated");
          }

          const { uploadUrl, s3Key, bucketName } = await getPresignedUrl(
            file.name,
            mimeType,
            "announcements",
            userData.id,
          );

          await uploadToS3Direct(uploadUrl, file, mimeType, (progress) => {
            setUploads((prev) =>
              prev.map((u) =>
                u.localId === localId ? { ...u, progress } : u,
              ),
            );
          });

          const confirmed = await confirmAttachmentInDB({
            relation_type: "announcements",
            relation_id: String(savedAnnouncementId),
            s3_key: s3Key,
            s3_bucket: bucketName,
            display_name: file.name,
            file_type: mimeType,
            file_size: file.size,
          });

          setUploads((prev) =>
            prev.map((u) =>
              u.localId === localId
                ? { ...u, status: "done", progress: 100, attachment: confirmed }
                : u,
            ),
          );

          setAttachments((prev) => [...prev, confirmed]);
          setTimeout(() => {
            setUploads((prev) => prev.filter((u) => u.localId !== localId));
          }, 1000);
        } catch (err: any) {
          setUploads((prev) =>
            prev.map((u) =>
              u.localId === localId
                ? { ...u, status: "error", error: err.message }
                : u,
            ),
          );
        }
      })();
    }
  };

  const handleAttachmentDelete = async (attachmentId: number | string) => {
    try {
      await announcementService.deleteAnnouncementAttachment(attachmentId);
      setAttachments((prev) =>
        prev.filter((a) => a.attachment_id !== attachmentId),
      );
    } catch (err) {
      console.error("Failed to delete attachment:", err);
    }
  };

  const handleAttachmentRename = async (
    attachmentId: number | string,
    newName: string,
  ) => {
    try {
      await announcementService.renameAttachment(attachmentId, newName);
      setAttachments((prev) =>
        prev.map((a) =>
          a.attachment_id === attachmentId
            ? { ...a, display_name: newName }
            : a,
        ),
      );
    } catch (err) {
      console.error("Failed to rename attachment:", err);
    }
  };

  const handleCancelUpload = (localId: string) => {
    setUploads((prev) => prev.filter((u) => u.localId !== localId));
  };

  const handleFilesPicked = async (files: SharepointFile[]) => {
    if (!savedAnnouncementId) return;

    const results = await Promise.allSettled(
      files.map((file) =>
        copySharepointFileToRelation(
          file.id,
          "announcements",
          savedAnnouncementId,
        ),
      ),
    );
    const succeeded = results
      .filter(
        (result): result is PromiseFulfilledResult<AnnouncementAttachment> =>
          result.status === "fulfilled",
      )
      .map((result) => result.value);

    if (succeeded.length > 0) {
      setAttachments((prev) => [...prev, ...succeeded]);
      setShowSavedBanner(true);
    }
    if (succeeded.length < files.length) {
      setError(t("feed.form.attach_from_files_error"));
    }
  };

  if (pageLoading) return <Spinner />;

  return (
    <>
    <div className="max-w-3xl mx-auto">
      {/* Floating saved banner */}
      {showSavedBanner && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl shadow-lg text-sm font-medium pointer-events-none">
          <svg
            className="w-4 h-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          {t("feed.form.saved_banner")}
        </div>
      )}
      {/* Back button + Page title */}
      <div className="mb-4 md:mb-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 active:text-gray-800 p-2 -ml-2 rounded-lg mb-2 md:mb-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t("common.back", "Back")}
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          {isEditing ? t("feed.form.edit_title") : t("feed.form.create_title")}
        </h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">

          <Input
            label={t("feed.form.title")}
            name="title"
            value={form.title}
            onChange={handleChange}
            required
          />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {t("feed.form.content")}
            </label>
            <textarea
              name="content"
              value={form.content}
              onChange={handleChange}
              rows={4}
              required
              className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="space-y-3 rounded-xl border border-gray-200 p-4">
            {form.poll === undefined ? (
              <button type="button" className="text-sm font-medium text-indigo-600 hover:text-indigo-800" onClick={() => setForm((prev) => ({ ...prev, poll: { question: "", options: [{ label: "", requires_note: false }, { label: "", requires_note: false }], allow_multiple: false, closes_at: null } }))}>
                + {t("feed.poll.add")}
              </button>
            ) : form.poll === null ? (
              <button type="button" className="text-sm font-medium text-indigo-600 hover:text-indigo-800" onClick={() => setForm((prev) => ({ ...prev, poll: { question: "", options: [{ label: "", requires_note: false }, { label: "", requires_note: false }], allow_multiple: false, closes_at: null } }))}>
                + {t("feed.poll.add")}
              </button>
            ) : <>
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-900">{t("feed.poll.add")}</h3>
                {!announcement?.poll?.has_responses && <button type="button" className="text-sm text-red-600 hover:text-red-800" onClick={() => setForm((prev) => ({ ...prev, poll: announcement?.poll ? null : undefined }))}>{t("feed.poll.remove")}</button>}
              </div>
              {announcement?.poll?.has_responses && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{t("feed.poll.frozen_hint")}</p>}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">{t("feed.poll.question")}</label>
                <input value={form.poll.question} maxLength={300} disabled={announcement?.poll?.has_responses} placeholder={t("feed.poll.question_placeholder")} onChange={(e) => setForm((prev) => prev.poll && typeof prev.poll === "object" ? { ...prev, poll: { ...prev.poll, question: e.target.value } } : prev)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
              </div>
              <div className="space-y-2">
                {form.poll.options.map((option, index) => <div key={index} className="flex items-center gap-2">
                  <input value={option.label} maxLength={100} disabled={announcement?.poll?.has_responses} placeholder={`${t("feed.poll.option")} ${index + 1}`} onChange={(e) => setForm((prev) => {
                    if (!prev.poll || typeof prev.poll !== "object") return prev;
                    const options = [...prev.poll.options]; options[index] = { ...options[index], label: e.target.value };
                    return { ...prev, poll: { ...prev.poll, options } };
                  })} className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
                  <label className="flex shrink-0 items-center gap-1 text-xs text-gray-600" title={t("feed.poll.require_note_hint")}>
                    <input type="checkbox" checked={option.requires_note} disabled={announcement?.poll?.has_responses} onChange={(e) => setForm((prev) => {
                      if (!prev.poll || typeof prev.poll !== "object") return prev;
                      const options = [...prev.poll.options]; options[index] = { ...options[index], requires_note: e.target.checked };
                      return { ...prev, poll: { ...prev.poll, options } };
                    })} />
                    {t("feed.poll.require_note")}
                  </label>
                  {!announcement?.poll?.has_responses && form.poll!.options.length > 2 && <button type="button" aria-label={t("feed.poll.remove_option")} onClick={() => setForm((prev) => prev.poll && typeof prev.poll === "object" ? { ...prev, poll: { ...prev.poll, options: prev.poll.options.filter((_, i) => i !== index) } } : prev)} className="px-2 text-xl text-gray-400 hover:text-red-600">×</button>}
                </div>)}
                {!announcement?.poll?.has_responses && form.poll.options.length < 10 && <button type="button" className="text-sm text-indigo-600 hover:text-indigo-800" onClick={() => setForm((prev) => prev.poll && typeof prev.poll === "object" ? { ...prev, poll: { ...prev.poll, options: [...prev.poll.options, { label: "", requires_note: false }] } } : prev)}>+ {t("feed.poll.add_option")}</button>}
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.poll.allow_multiple ?? false} disabled={announcement?.poll?.has_responses} onChange={(e) => setForm((prev) => prev.poll && typeof prev.poll === "object" ? { ...prev, poll: { ...prev.poll, allow_multiple: e.target.checked } } : prev)} />{t("feed.poll.allow_multiple")}</label>
              <div className="space-y-1"><label className="block text-sm font-medium text-gray-700">{t("feed.poll.closes_at")}</label><input type="datetime-local" value={form.poll.closes_at || ""} onChange={(e) => setForm((prev) => prev.poll && typeof prev.poll === "object" ? { ...prev, poll: { ...prev.poll, closes_at: e.target.value || null } } : prev)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
              {pollErrors.map((message) => <p key={message} className="text-sm text-red-600">{message}</p>)}
            </>}
          </div>

          {!isSouser && <>
{/* Company searchable multi-select */}
          {!isSouser && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                {t("feed.form.companies")}
              </label>

              {/* Selected chips */}
              {form.company.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {form.company.map((val, idx) => {
                    const strVal =
                      typeof val === "string"
                        ? val
                        : String((val as any)?.value ?? (val as any)?.label ?? idx);
                    const match = companies.find((c) => c.value === strVal);
                    const displayLabel =
                      match?.label ||
                      (typeof val === "string"
                        ? val
                        : ((val as any)?.label ?? (val as any)?.value ?? strVal));
                    return (
                      <span
                        key={strVal}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700"
                      >
                        {displayLabel}
                        <button
                          type="button"
                          onClick={() => handleCompanyToggle(strVal)}
                          className="hover:text-indigo-900"
                        >
                          &times;
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Dropdown */}
              <div className="relative" ref={companyDropdownRef}>
                <input
                  type="text"
                  value={companySearch}
                  onChange={(e) => {
                    setCompanySearch(e.target.value);
                    if (!companyDropdownOpen) setCompanyDropdownOpen(true);
                  }}
                  onFocus={() => setCompanyDropdownOpen(true)}
                  placeholder={t("feed.form.search_company", "Search company...")}
                  className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />

                {companyDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                    {companies
                      .filter((c) =>
                        c.label?.toLowerCase().includes(companySearch.toLowerCase()),
                      )
                      .map((c) => {
                        const isSelected = form.company.includes(c.value);
                        return (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => {
                              handleCompanyToggle(c.value);
                              setCompanySearch("");
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2.5 md:py-2 text-base md:text-sm transition-colors ${
                              isSelected
                                ? "bg-indigo-50 text-indigo-700"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <span
                              className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${
                                isSelected
                                  ? "bg-indigo-600 border-indigo-600 text-white"
                                  : "border-gray-300"
                              }`}
                            >
                              {isSelected && "\u2713"}
                            </span>
                            {c.label}
                          </button>
                        );
                      })}
                    {companies.filter((c) =>
                      c.label?.toLowerCase().includes(companySearch.toLowerCase()),
                    ).length === 0 && (
                      <p className="px-3 py-2 text-xs text-gray-400 italic">
                        {t("common.no_results", "No results found")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Batch selector */}
          {form.company.length > 0 && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                {t("feed.form.batch")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="batch_no"
                value={form.batch_no || ""}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                  handleChange(e);
                }}
                placeholder={t("feed.form.batch_placeholder")}
                className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}

          {/* Country searchable multi-select */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {t("feed.form.countries_label")}
            </label>

            {form.country.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {form.country.map((code) => {
                  const opt = COUNTRY_OPTIONS.find((c) => c.code === code);
                  return (
                    <span
                      key={code}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        isSouser
                          ? "bg-gray-100 text-gray-700"
                          : "bg-indigo-100 text-indigo-700"
                      }`}
                    >
                      {opt ? t(`profile.${opt.key}`) : code}
                      {!isSouser && (
                        <button
                          type="button"
                          onClick={() => handleCountryToggle(code)}
                          className="hover:text-indigo-900"
                        >
                          &times;
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            <div className="relative" ref={countryDropdownRef}>
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => {
                  if (!isSouser) {
                    setCountrySearch(e.target.value);
                    if (!countryDropdownOpen) setCountryDropdownOpen(true);
                  }
                }}
                onFocus={() => !isSouser && setCountryDropdownOpen(true)}
                placeholder={t("feed.form.search_country")}
                disabled={isSouser}
                className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none ${
                  isSouser
                    ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                    : "border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                }`}
              />

              {countryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                  {COUNTRY_OPTIONS.filter((opt) =>
                    t(`profile.${opt.key}`)
                      .toLowerCase()
                      .includes(countrySearch.toLowerCase()),
                  ).map((opt) => {
                    const isSelected = form.country.includes(opt.code);
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => {
                          handleCountryToggle(opt.code);
                          setCountrySearch("");
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 md:py-2 text-base md:text-sm transition-colors ${
                          isSelected
                            ? "bg-indigo-50 text-indigo-700"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${
                            isSelected
                              ? "bg-indigo-600 border-indigo-600 text-white"
                              : "border-gray-300"
                          }`}
                        >
                          {isSelected && "✓"}
                        </span>
                        {t(`profile.${opt.key}`)}
                      </button>
                    );
                  })}
                  {COUNTRY_OPTIONS.filter((opt) =>
                    t(`profile.${opt.key}`)
                      .toLowerCase()
                      .includes(countrySearch.toLowerCase()),
                  ).length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-400 italic">
                      {t("common.no_results", "No results found")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sending org — single select, only when exactly 1 country is selected (or if souser) */}
          {(form.country.length === 1 || isSouser) && sendingOrgs.length > 0 && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                {t("feed.form.sending_org")}
              </label>
              <select
                name="sending_org"
                value={form.sending_org || ""}
                onChange={handleChange}
                disabled={isSouser}
                className={`w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none ${
                  isSouser
                    ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                    : "border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                }`}
              >
                <option value="">{t("feed.form.sending_org_all")}</option>
                {sendingOrgs.map((org) => (
                  <option key={org.value} value={org.value}>
                    {org.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          </>}
{isSouser && <p className="text-sm text-gray-500">{t("souser.bulletin_audience", { org: userData?.souser_sending_org })}</p>}
          {/* Audience preview */}
          <AudiencePreview count={audienceCount} officersOnly={audienceOfficersOnly} loading={audienceLoading} />

          {/* Date range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label={t("feed.form.date_from")}
              name="date_from"
              type="date"
              value={typeof form.date_from === "string" ? form.date_from : ""}
              onChange={handleChange}
            />
            <Input
              label={t("feed.form.date_to")}
              name="date_to"
              type="date"
              value={typeof form.date_to === "string" ? form.date_to : ""}
              onChange={handleChange}
            />
          </div>

          {/* Toggles */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 py-1">
              <input
                type="checkbox"
                name="active"
                checked={form.active}
                onChange={handleChange}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {t("feed.form.active")}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 py-1">
              <input
                type="checkbox"
                name="comments_on"
                checked={form.comments_on}
                onChange={handleChange}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {t("feed.form.comments_on")}
            </label>
          </div>

          {/* Attachments section (visible after save) */}
          {savedAnnouncementId && (
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700">
                {t("feed.form.attachments")}
              </label>

              <AttachmentDropZone
                onFilesSelected={handleFilesSelected}
                accept={ACCEPTED_ATTACHMENT_EXTENSIONS}
                disabled={uploads.some((u) => u.status === "uploading")}
              />

              <Button
                type="button"
                variant="secondary"
                onClick={() => setFilesPickerOpen(true)}
                className="text-sm w-full"
              >
                {t("feed.form.choose_from_files")}
              </Button>

              <AttachmentList
                attachments={attachments}
                uploads={uploads}
                onDelete={handleAttachmentDelete}
                onRename={handleAttachmentRename}
                onCancelUpload={handleCancelUpload}
              />
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between pt-2">
            {savedAnnouncementId ? (
              <>
                <Button
                  variant="danger"
                  type="button"
                  loading={deleting}
                  onClick={handleDelete}
                  className="w-full sm:w-auto"
                >
                  {t("common.delete", "Delete")}
                </Button>
                <div className="flex flex-col-reverse gap-3 sm:flex-row">
                  <Button variant="secondary" type="button" onClick={() => navigate(-1)} className="w-full sm:w-auto">
                    {t("common.done")}
                  </Button>
                  <Button type="submit" loading={saving} className="w-full sm:w-auto">
                    {t("common.save")}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end w-full">
                <Button variant="secondary" type="button" onClick={() => navigate(-1)} className="w-full sm:w-auto">
                  {t("common.cancel")}
                </Button>
                <Button type="submit" loading={saving} className="w-full sm:w-auto">
                  {isEditing ? t("common.save") : t("common.create")}
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
    <FilesPickerModal
      isOpen={filesPickerOpen}
      onClose={() => setFilesPickerOpen(false)}
      onSelect={handleFilesPicked}
    />
    </>
  );
};
